import { create } from 'zustand';

export type MuscleGroup = 
  | 'chest' 
  | 'back' 
  | 'shoulders' 
  | 'quads' 
  | 'hamstrings' 
  | 'calves' 
  | 'biceps' 
  | 'triceps' 
  | 'core'
  | 'glutes'
  | 'forearms'
  | 'lower_back';

export type Equipment = 'Mancuerna' | 'Barra' | 'Peso Corporal' | 'Máquina' | 'Cable';

export interface Exercise {
  id: string;
  name: string;
  muscle: MuscleGroup;
  difficulty: 'Principiante' | 'Intermedio' | 'Avanzado';
  equipment: Equipment;
  instructions: string[];
  animationUrl?: string; // Placeholder for visual guide
  image?: string;
}

export interface GymProduct {
  id: string;
  name: string;
  category: 'Ropa' | 'Nutrición' | 'Comunidad';
  price: number;
  image: string;
  description: string;
  trainerId?: string;
}

export interface Routine {
  id: string;
  name: string;
  description: string;
  difficulty: 'Principiante' | 'Intermedio' | 'Avanzado';
  duration: string; // e.g. "45 min"
  exerciseIds: string[];
  image: string;
  category?: 'Gym' | 'Casa';
  trainerId?: string;
}

export interface Trainer {
  id: string;
  name: string;
  specialty: string;
  bio: string;
  experience: string;
  image: string;
  socials?: {
    instagram?: string;
    twitter?: string;
  };
}

export type Gender = 'male' | 'female';
export type BodyView = 'front' | 'back';

interface GymState {
  gender: Gender;
  bodyView: BodyView;
  selectedMuscle: MuscleGroup | null;
  selectedExercise: Exercise | null;
  selectedRoutine: Routine | null;
  selectedTrainer: Trainer | null;
  hasLoyaltyCard: boolean;
  loyaltyPoints: number;
  
  // Actions
  setGender: (gender: Gender) => void;
  setBodyView: (view: BodyView) => void;
  setSelectedMuscle: (muscle: MuscleGroup | null) => void;
  setSelectedExercise: (exercise: Exercise | null) => void;
  setSelectedRoutine: (routine: Routine | null) => void;
  setSelectedTrainer: (trainer: Trainer | null) => void;
  toggleLoyaltyCard: () => void;
  addPoints: (points: number) => void;
}

export const useGymStore = create<GymState>((set) => ({
  gender: 'male',
  bodyView: 'front',
  selectedMuscle: null,
  selectedExercise: null,
  selectedRoutine: null,
  selectedTrainer: null,
  hasLoyaltyCard: false,
  loyaltyPoints: 150, // Starting points

  setGender: (gender) => set({ gender, selectedMuscle: null, selectedExercise: null, selectedRoutine: null }),
  setBodyView: (bodyView) => set({ bodyView, selectedMuscle: null }),
  setSelectedMuscle: (muscle) => set({ selectedMuscle: muscle, selectedExercise: null, selectedRoutine: null }),
  setSelectedExercise: (exercise) => set({ selectedExercise: exercise, selectedRoutine: null }),
  setSelectedRoutine: (routine) => set({ selectedRoutine: routine, selectedExercise: null, selectedMuscle: null }),
  setSelectedTrainer: (trainer) => set({ selectedTrainer: trainer }),
  toggleLoyaltyCard: () => set((state) => ({ hasLoyaltyCard: !state.hasLoyaltyCard })),
  addPoints: (points) => set((state) => ({ loyaltyPoints: state.loyaltyPoints + points })),
}));

export const GYM_TRAINERS: Trainer[] = [
  {
    id: 'coach-alex',
    name: 'Alex Rivera',
    specialty: 'Hipertrofia & Fuerza',
    experience: '10+ años de experiencia',
    bio: 'Especialista en transformaciones corporales y optimización metabólica. Ex-atleta de competición.',
    image: 'https://images.unsplash.com/photo-1567013127542-490d757e51fc?q=80&w=800&auto=format&fit=crop'
  },
  {
    id: 'coach-sarah',
    name: 'Sarah Connor',
    specialty: 'Nutrición & Funcional',
    experience: '8 años de experiencia',
    bio: 'Certificada en nutrición deportiva. Ayuda a integrar el fitness en estilos de vida ocupados.',
    image: 'https://images.unsplash.com/photo-1548690312-e3b507d17a4d?q=80&w=800&auto=format&fit=crop'
  },
  {
    id: 'coach-marcos',
    name: 'Marcos Bolt',
    specialty: 'Atleta Pro & HIIT',
    experience: '12 años de experiencia',
    bio: 'Fisioterapeuta y entrenador de alto rendimiento. Experto en movilidad y prevención de lesiones.',
    image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=800&auto=format&fit=crop'
  }
];

export const GYM_ROUTINES: Routine[] = [
  {
    id: 'full-body-burn',
    name: 'Full Body Burn',
    description: 'Una rutina intensa que trabaja todos los grupos musculares principales para maximizar la quema de calorías.',
    difficulty: 'Intermedio',
    duration: '45 min',
    exerciseIds: ['push-ups', 'squats', 'plank', 'bent-over-row'],
    image: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?q=80&w=800&auto=format&fit=crop',
    category: 'Gym',
    trainerId: 'coach-alex'
  },
  {
    id: 'strength-starter',
    name: 'Fuerza Iniciación',
    description: 'Perfecto para quienes comienzan. Se enfoca en la técnica correcta de los movimientos fundamentales.',
    difficulty: 'Principiante',
    duration: '30 min',
    exerciseIds: ['push-ups', 'squats', 'plank'],
    image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=800&auto=format&fit=crop',
    category: 'Gym',
    trainerId: 'coach-alex'
  },
  {
    id: 'home-hiit-blast',
    name: 'HIIT Explosivo en Casa',
    description: 'Sin equipo, alta intensidad. Quema grasa desde la comodidad de tu sala.',
    difficulty: 'Intermedio',
    duration: '20 min',
    exerciseIds: ['push-ups', 'squats', 'plank'],
    image: 'https://images.unsplash.com/photo-1571019623129-fbf8a469f06d?q=80&w=800&auto=format&fit=crop',
    category: 'Casa',
    trainerId: 'coach-marcos'
  },
  {
    id: 'yoga-flow-morning',
    name: 'Yoga Flow Mañanero',
    description: 'Despierta tu cuerpo y mente con esta secuencia de movilidad y estiramiento.',
    difficulty: 'Principiante',
    duration: '25 min',
    exerciseIds: ['plank'],
    image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?q=80&w=800&auto=format&fit=crop',
    category: 'Casa',
    trainerId: 'coach-sarah'
  }
];

export const GYM_PRODUCTS: GymProduct[] = [
  // Gym Shop - Clothing
  {
    id: 'comp-shirt',
    name: 'Camisera Compresión Bienvenido',
    category: 'Ropa',
    price: 35,
    image: 'https://images.unsplash.com/photo-1581009146145-b5ef03a7403f?q=80&w=800&auto=format&fit=crop',
    description: 'Tela técnica de alto rendimiento para entrenamientos intensos.',
    trainerId: 'coach-alex'
  },
  {
    id: 'pro-shorts',
    name: 'Shorts Pro-Performance',
    category: 'Ropa',
    price: 28,
    image: 'https://images.unsplash.com/photo-1591195853828-11db59a44f6b?q=80&w=800&auto=format&fit=crop',
    description: 'Flexibilidad total para sentadillas y movimientos explosivos.',
    trainerId: 'coach-marcos'
  },
  {
    id: 'kensu-hoodie',
    name: 'Sudadera Bienvenido Oversize',
    category: 'Ropa',
    price: 55,
    image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?q=80&w=800&auto=format&fit=crop',
    description: 'Estilo y comodidad para el pre-entreno y el día a día.',
    trainerId: 'coach-alex'
  },
  
  // Nutrition Store - Athlete Food
  {
    id: 'whey-isolate',
    name: 'Proteína Whey Isolate 2kg',
    category: 'Nutrición',
    price: 65,
    image: 'https://images.unsplash.com/photo-1593095948071-474c5cc2989d?q=80&w=800&auto=format&fit=crop',
    description: 'Máxima pureza para la recuperación muscular inmediata.',
    trainerId: 'coach-sarah'
  },
  {
    id: 'athlete-oats',
    name: 'Avena Instantánea para Atletas',
    category: 'Nutrición',
    price: 12,
    image: 'https://images.unsplash.com/photo-1586444248902-2f64eddf13cf?q=80&w=800&auto=format&fit=crop',
    description: 'Carbohidratos de bajo índice glucémico para energía duradera.',
    trainerId: 'coach-sarah'
  },
  {
    id: 'pre-workout-kensu',
    name: 'Pre-Entreno Bienvenido Beast',
    category: 'Nutrición',
    price: 40,
    image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?q=80&w=800&auto=format&fit=crop',
    description: 'Fórmula explosiva para sesiones de alta intensidad.',
    trainerId: 'coach-marcos'
  },

  // Social / Community Activities
  {
    id: 'running-club',
    name: 'Running Club Bienvenido',
    category: 'Comunidad',
    price: 0,
    image: 'https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?q=80&w=800&auto=format&fit=crop',
    description: 'Salidas semanales en grupo por la ciudad. Solo para miembros.',
    trainerId: 'coach-marcos'
  },
  {
    id: 'zumba-class',
    name: 'Masterclass Zumba',
    category: 'Comunidad',
    price: 5,
    image: 'https://images.unsplash.com/photo-1518611012118-696072aa579a?q=80&w=800&auto=format&fit=crop',
    description: 'Energía y ritmo para quemar grasa divirtiéndote en comunidad.',
    trainerId: 'coach-sarah'
  },
  {
    id: 'fitcombat-session',
    name: 'FitCombat Experience',
    category: 'Comunidad',
    price: 10,
    image: 'https://images.unsplash.com/photo-1549476464-37392f71752a?q=80&w=800&auto=format&fit=crop',
    description: 'Entrenamiento de combate sin contacto para potenciar tu cardio.',
    trainerId: 'coach-marcos'
  }
];

export const EXERCISES: Exercise[] = [
  // Chest
  {
    id: 'push-ups',
    name: 'Push Ups',
    muscle: 'chest',
    difficulty: 'Principiante',
    equipment: 'Peso Corporal',
    image: 'https://images.unsplash.com/photo-1571019623129-fbf8a469f06d?q=80&w=800&auto=format&fit=crop',
    instructions: [
      'Coloca las manos un poco más anchas que los hombros.',
      'Mantén el cuerpo en línea recta desde la cabeza hasta los talones.',
      'Baja el cuerpo hasta que el pecho casi toque el suelo.',
      'Empuja hacia atrás a la posición inicial.'
    ]
  },
  {
    id: 'bench-press',
    name: 'Press de Banca con Barra',
    muscle: 'chest',
    difficulty: 'Intermedio',
    equipment: 'Barra',
    image: 'https://images.unsplash.com/photo-1541534741688-6078c64b52d2?q=80&w=800&auto=format&fit=crop',
    instructions: [
      'Acuéstate en un banco plano.',
      'Sujeta la barra con un agarre un poco más ancho que los hombros.',
      'Baja la barra lentamente hasta la mitad del pecho.',
      'Empuja la barra hacia arriba extendiendo los brazos.'
    ]
  },
  {
    id: 'dumbbell-chest-flys',
    name: 'Aperturas con Mancuernas',
    muscle: 'chest',
    difficulty: 'Principiante',
    equipment: 'Mancuerna',
    image: 'https://images.unsplash.com/photo-1583454110551-21f2fa2adfcd?q=80&w=800&auto=format&fit=crop',
    instructions: [
      'Acuéstate en un banco plano con las mancuernas sobre el pecho.',
      'Baja los brazos hacia los lados en un arco amplio.',
      'Mantén una ligera flexión en los codos.',
      'Lleva las mancuernas de vuelta a la posición inicial.'
    ]
  },
  {
    id: 'chest-dip',
    name: 'Fondos de Pecho',
    muscle: 'chest',
    difficulty: 'Avanzado',
    equipment: 'Peso Corporal',
    instructions: [
      'Sujétate en las barras paralelas con los brazos extendidos.',
      'Inclina el torso hacia adelante ligeramente.',
      'Baja el cuerpo flexionando los brazos.',
      'Empuja hacia arriba con fuerza.'
    ]
  },
  // Back
  {
    id: 'pull-ups',
    name: 'Dominadas',
    muscle: 'back',
    difficulty: 'Avanzado',
    equipment: 'Peso Corporal',
    image: 'https://images.unsplash.com/photo-1598971639058-aba3cba21cab?q=80&w=800&auto=format&fit=crop',
    instructions: [
      'Sujeta la barra con las palmas hacia afuera.',
      'Tira de tu cuerpo hacia arriba hasta que la barbilla pase la barra.',
      'Baja el cuerpo de forma controlada.'
    ]
  },
  {
    id: 'bent-over-row',
    name: 'Remo con Barra',
    muscle: 'back',
    difficulty: 'Intermedio',
    equipment: 'Barra',
    instructions: [
      'Inclínate hacia adelante manteniendo la espalda recta.',
      'Sujeta la barra con las palmas hacia abajo.',
      'Tira de la barra hacia tu abdomen.',
      'Baja la barra lentamente.'
    ]
  },
  {
    id: 'lat-pulldown',
    name: 'Jalón al Pecho',
    muscle: 'back',
    difficulty: 'Principiante',
    equipment: 'Máquina',
    instructions: [
      'Siéntate en la máquina de jalón.',
      'Sujeta la barra con un agarre amplio.',
      'Tira de la barra hacia la parte superior del pecho.',
      'Regresa la barra lentamente.'
    ]
  },
  {
    id: 'deadlift',
    name: 'Peso Muerto',
    muscle: 'back',
    difficulty: 'Avanzado',
    equipment: 'Barra',
    image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=800&auto=format&fit=crop',
    instructions: [
      'Colócate frente a la barra con los pies a la anchura de las caderas.',
      'Baja y sujeta la barra manteniendo la espalda recta.',
      'Levanta la barra extendiendo caderas y rodillas.',
      'Baja la barra siguiendo el mismo camino invertido.'
    ]
  },
  // Shoulders
  {
    id: 'overhead-press',
    name: 'Press Militar',
    muscle: 'shoulders',
    difficulty: 'Intermedio',
    equipment: 'Barra',
    image: 'https://images.unsplash.com/photo-1541534458328-ef582483f9cf?q=80&w=800&auto=format&fit=crop',
    instructions: [
      'Sujeta la barra a la altura de los hombros.',
      'Empuja la barra hacia arriba por encima de tu cabeza.',
      'Baja la barra de forma controlada.'
    ]
  },
  {
    id: 'lateral-raises',
    name: 'Elevaciones Laterales',
    muscle: 'shoulders',
    difficulty: 'Principiante',
    equipment: 'Mancuerna',
    instructions: [
      'De pie con mancuernas a los lados.',
      'Eleva los brazos hacia los lados hasta la altura de los hombros.',
      'Mantén una ligera flexión en los codos.',
      'Baja lentamente.'
    ]
  },
  {
    id: 'face-pulls',
    name: 'Face Pulls',
    muscle: 'shoulders',
    difficulty: 'Principiante',
    equipment: 'Cable',
    instructions: [
      'Coloca la polea a la altura del rostro.',
      'Tira de la cuerda hacia tu cara, llevando los codos hacia atrás.',
      'Separa los extremos de la cuerda al final del movimiento.',
      'Regresa lentamente.'
    ]
  },
  // Biceps
  {
    id: 'bicep-curls',
    name: 'Curl de Bíceps',
    muscle: 'biceps',
    difficulty: 'Principiante',
    equipment: 'Mancuerna',
    instructions: [
      'Sujeta las mancuernas con las palmas hacia adelante.',
      'Flexiona los codos llevando las pesas hacia los hombros.',
      'Mantén los codos pegados al cuerpo.',
      'Baja lentamente.'
    ]
  },
  {
    id: 'hammer-curls',
    name: 'Curl Martillo',
    muscle: 'biceps',
    difficulty: 'Principiante',
    equipment: 'Mancuerna',
    instructions: [
      'Sujeta las mancuernas con las palmas enfrentadas (agarre neutro).',
      'Flexiona los codos llevando las pesas hacia arriba.',
      'Mantén la posición de las palmas constante.',
      'Baja lentamente.'
    ]
  },
  // Triceps
  {
    id: 'tricep-pushdown',
    name: 'Extensión de Tríceps en Polea',
    muscle: 'triceps',
    difficulty: 'Principiante',
    equipment: 'Cable',
    instructions: [
      'Sujeta la barra de la polea alta con las palmas hacia abajo.',
      'Mantén los codos pegados a los costados.',
      'Extiende los brazos hacia abajo hasta bloquear.',
      'Regresa lentamente a la posición inicial.'
    ]
  },
  {
    id: 'skull-crushers',
    name: 'Press Francés',
    muscle: 'triceps',
    difficulty: 'Intermedio',
    equipment: 'Barra',
    instructions: [
      'Acuéstate en un banco plano con una barra EZ.',
      'Extiende los brazos sobre el pecho.',
      'Baja la barra hacia tu frente flexionando los codos.',
      'Extiende los brazos de nuevo a la posición inicial.'
    ]
  },
  // Legs (Quads)
  {
    id: 'squats',
    name: 'Sentadillas con Barra',
    muscle: 'quads',
    difficulty: 'Principiante',
    equipment: 'Barra',
    image: 'https://images.unsplash.com/photo-1534258936925-c58bed479fcb?q=80&w=800&auto=format&fit=crop',
    instructions: [
      'Coloca la barra sobre el trapecio superior.',
      'Baja las caderas como si te sentaras en una silla.',
      'Mantén la espalda recta y el pecho arriba.',
      'Empuja a través de los talones para volver arriba.'
    ]
  },
  {
    id: 'leg-press',
    name: 'Prensa de Piernas',
    muscle: 'quads',
    difficulty: 'Principiante',
    equipment: 'Máquina',
    instructions: [
      'Siéntate en la máquina de prensa de piernas.',
      'Coloca los pies a la anchura de los hombros sobre la plataforma.',
      'Baja la plataforma doblando las rodillas.',
      'Empuja la plataforma hacia arriba sin bloquear las rodillas.'
    ]
  },
  // Hamstrings
  {
    id: 'leg-curls',
    name: 'Curl Femoral',
    muscle: 'hamstrings',
    difficulty: 'Principiante',
    equipment: 'Máquina',
    instructions: [
      'Túmbate en la máquina de curl femoral (o sentado).',
      'Flexiona las piernas llevando los talones hacia los glúteos.',
      'Contrae los isquiotibiales en la parte superior.',
      'Baja el peso de forma controlada.'
    ]
  },
  {
    id: 'romanian-deadlift',
    name: 'Peso Muerto Rumano',
    muscle: 'hamstrings',
    difficulty: 'Intermedio',
    equipment: 'Barra',
    instructions: [
      'Sujeta la barra frente a ti.',
      'Baja la barra manteniendo las piernas casi rectas e inclinando la cadera.',
      'Siente el estiramiento en los isquiotibiales.',
      'Vuelve a la posición inicial contrayendo los glúteos.'
    ]
  },
  // Calves
  {
    id: 'calf-raises',
    name: 'Elevación de Talones',
    muscle: 'calves',
    difficulty: 'Principiante',
    equipment: 'Máquina',
    instructions: [
      'Colóca los pies en la máquina de gemelos.',
      'Eleva los talones lo más posible.',
      'Baja los talones lentamente para estirar el gemelo.',
      'Repite de forma rítmica.'
    ]
  },
  // Core
  {
    id: 'plank',
    name: 'Plancha Abdominal',
    muscle: 'core',
    difficulty: 'Principiante',
    equipment: 'Peso Corporal',
    instructions: [
      'Apóyate en los antebrazos y los dedos de los pies.',
      'Mantén el cuerpo en línea recta.',
      'Contrae el abdomen y mantén la posición.'
    ]
  },
  {
    id: 'hanging-leg-raises',
    name: 'Elevación de Piernas Colgado',
    muscle: 'core',
    difficulty: 'Intermedio',
    equipment: 'Peso Corporal',
    instructions: [
      'Cuélgate de una barra de dominadas.',
      'Eleva las piernas rectas (o rodillas al pecho) hacia arriba.',
      'Baja las piernas de forma controlada sin balancearte.'
    ]
  },
  // Glutes
  {
    id: 'hip-thrust',
    name: 'Hip Thrust con Barra',
    muscle: 'glutes',
    difficulty: 'Intermedio',
    equipment: 'Barra',
    instructions: [
      'Apoya la espalda alta en un banco.',
      'Coloca la barra sobre la cadera.',
      'Eleva la cadera contrayendo los glúteos.',
      'Baja de forma controlada.'
    ]
  },
  // Forearms
  {
    id: 'wrist-curls',
    name: 'Curl de Muñeca',
    muscle: 'forearms',
    difficulty: 'Principiante',
    equipment: 'Mancuerna',
    instructions: [
      'Apoya el antebrazo sobre un banco o muslo.',
      'Flexiona la muñeca hacia arriba.',
      'Baja lentamente.'
    ]
  },
  // Lower Back
  {
    id: 'hyperextensions',
    name: 'Hiperextensiones',
    muscle: 'lower_back',
    difficulty: 'Principiante',
    equipment: 'Máquina',
    instructions: [
      'Colócate en el banco de extensiones.',
      'Baja el torso flexionando la cadera.',
      'Eleva el torso hasta que el cuerpo esté recto.',
      'No arquees la espalda en exceso.'
    ]
  }
];
